import { Container, Graphics, type Sprite } from 'pixi.js';
import type { EntityId, SimEvent, SimState } from '../../sim/state';
import { BURST_AT, bossCrackStage, farewellPose } from '../boss-moments';
import type { Family, RenderContext } from '../context';
import type { EnemyPose } from '../enemies';
import { BOSS_SCALE, FACE_RADIUS, MASK_BODY } from '../face-kit';
import type { Frame } from '../frame';
import { WHITE, circle, glow, paint, type Shape } from '../paint';
import { add, hide, setTint } from '../util';
import { ViewPool } from '../views';
import { THIRD_EYE_AT } from './canicule-face';
import { type Sun, createSunFloor } from './canicule-floor';
import { EXIT_BEATS, FLAME, GROWN, coolWave, exitFlame, opening } from './canicule-moments';
import { placeOnMask } from './on-mask';

const KIND = 'canicule';
const FINE = 2;
const HEAT_REACH = 150;
const AURA = { tint: 0xff9650, alpha: 0.16, lightBeats: 2 };
const GOLD = '#ffd9a0';
const COOL = 0x3fb8d0;
const FLIGHT = { tokens: ['tank', 'noyau', 'mage'] as const, scale: 0.7 };

interface Boss {
  readonly glow: Sprite;
  readonly eye: Sprite;
  landed: number;
  openedAt: number;
}

interface Exit {
  readonly start: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly tilt: number;
  readonly scale: number;
  readonly flames: readonly Sprite[];
}

interface Looks {
  readonly eye: Shape;
  readonly flame: Shape;
}

function paintLooks(): Looks {
  return {
    eye: paint(16 * FINE, 16 * FINE, 3 * FINE, (ctx) => {
      ctx.scale(FINE, FINE);
      circle(ctx, 3);
      ctx.fillStyle = MASK_BODY;
      ctx.fill();
      glow(ctx, GOLD, 8);
      circle(ctx, 1.8);
      ctx.fillStyle = GOLD;
      ctx.fill();
    }),
    flame: paint(40 * FINE, 40 * FINE, 12 * FINE, (ctx) => {
      ctx.scale(FINE, FINE);
      const [length, width] = [24, 0.25];
      const pol = (radius: number, angle: number): [number, number] => [
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
      ];
      ctx.beginPath();
      ctx.moveTo(...pol(0, -width));
      ctx.bezierCurveTo(
        ...pol(length * 0.4, -width * 0.6 + 0.06),
        ...pol(length * 0.75, 0.1),
        ...pol(length, 0.15),
      );
      ctx.bezierCurveTo(
        ...pol(length * 0.7, 0.13),
        ...pol(length * 0.35, width * 0.9),
        ...pol(0, width),
      );
      ctx.closePath();
      ctx.fillStyle = WHITE;
      ctx.fill();
    }),
  };
}

export function createCanicule(
  ctx: RenderContext,
  poseOf: (id: EntityId) => EnemyPose | undefined,
): Family {
  const { layers, textures } = ctx;
  const looks = paintLooks();
  const floor = createSunFloor(ctx);
  const glows = layers.glow.addChild(new Container());
  const eyes = layers.enemies.addChild(new Container());
  const exitEyes = layers.farewells.addChild(new Container());
  const sky = layers.fx.addChild(new Graphics());
  const flights = layers.fx.addChild(new Container());
  const exits: Exit[] = [];
  const spareFlames: (readonly Sprite[])[] = [];
  const heatEffect = ctx.specials.get(KIND);
  const reach = heatEffect?.kind === 'heat' ? heatEffect.radius : HEAT_REACH;
  const suns: Sun[] = [];

  const bosses = new ViewPool<Boss>(
    () => ({
      glow: add(glows, textures.halo),
      eye: add(eyes, looks.eye),
      landed: Number.NaN,
      openedAt: Number.NaN,
    }),
    (boss) => {
      hide(boss.glow, boss.eye);
      boss.landed = boss.openedAt = Number.NaN;
    },
  );

  function shine(boss: Boss, pose: EnemyPose, hurt: boolean, beats: number, frame: Frame): void {
    if (Number.isNaN(boss.landed)) {
      boss.landed = beats;
    }
    if (hurt && Number.isNaN(boss.openedAt)) {
      boss.openedAt = beats;
    }
    const strength = Math.min(1, (beats - boss.landed) / AURA.lightBeats);
    const { glow: aura } = boss;
    aura.visible = true;
    setTint(aura, AURA.tint);
    aura.position.set(pose.x, pose.y);
    aura.scale.set(reach / textures.halo.radius);
    aura.alpha = AURA.alpha * strength * frame.light.haloAlpha;
    suns.push({ x: pose.x, y: pose.y, reach, strength });
    const open = hurt ? opening(beats - boss.openedAt) : 0;
    const grown = 1 + (GROWN - 1) * open;
    pose.sprite.scale.set(pose.sprite.scale.x * grown, pose.sprite.scale.y * grown);
    boss.eye.visible = open > 0;
    if (open > 0) {
      placeOnMask(boss.eye, pose, eyes, THIRD_EYE_AT, FINE);
      boss.eye.alpha = open * pose.sprite.alpha;
    }
  }

  function leave(exit: Exit, frame: Frame): boolean {
    const beats = (frame.show - exit.start) / frame.tempo.ticksPerBeat;
    const p = (beats - BURST_AT) / EXIT_BEATS;
    if (p >= 1) {
      return false;
    }
    const { x, y, radius, flames } = exit;
    const { palette } = frame;
    const pose = farewellPose(beats, frame.calm);
    if (p < 0) {
      hide(...flames);
      return true;
    }
    const wave = coolWave(p);
    sky.circle(x, y, wave.radius * radius).fill({ color: COOL, alpha: wave.alpha });
    flames.forEach((sprite, index) => {
      const flight = exitFlame(index, p);
      sprite.visible = true;
      setTint(sprite, palette[FLIGHT.tokens[index % FLIGHT.tokens.length] ?? 'noyau']);
      sprite.position.set(x + flight.x * radius, y + flight.y * radius);
      sprite.rotation = flight.turn;
      sprite.scale.set((FLIGHT.scale * radius) / FACE_RADIUS / FINE);
      sprite.alpha = flight.alpha * (pose.phase === 'gone' ? 1 : 0);
    });
    return true;
  }

  return {
    update(state: SimState, alpha: number, frame: Frame): void {
      sky.clear();
      suns.length = 0;
      bosses.begin();
      const beats = frame.now / frame.tempo.ticksPerBeat;
      for (const enemy of state.enemies) {
        const pose = enemy.kind === KIND ? poseOf(enemy.id) : undefined;
        if (pose !== undefined) {
          const hurt = bossCrackStage(enemy.hp, enemy.maxHp) >= 1;
          shine(bosses.acquire(enemy.id), pose, hurt, beats, frame);
        }
      }
      bosses.end();
      floor.update(state, alpha, frame, suns);
      for (let index = exits.length - 1; index >= 0; index -= 1) {
        const exit = exits[index];
        if (exit !== undefined && !leave(exit, frame)) {
          hide(...exit.flames);
          spareFlames.push(exit.flames);
          exits.splice(index, 1);
        }
      }
    },
    onEvent(event: SimEvent, _state: SimState, frame: Frame): void {
      if (event.type !== 'enemyDied' || event.kind !== KIND) {
        return;
      }
      const pose = poseOf(event.id);
      const radius = pose?.radius ?? ctx.enemyRadii?.get(KIND) ?? FACE_RADIUS;
      layers.farewells.addChild(exitEyes);
      exits.push({
        start: frame.show,
        x: event.x,
        y: event.y,
        radius,
        tilt: pose?.tilt ?? 0,
        scale: radius / (FACE_RADIUS * BOSS_SCALE),
        flames:
          spareFlames.pop() ??
          Array.from({ length: FLAME.count }, () => add(flights, looks.flame, 0)),
      });
    },
    reset(): void {
      bosses.releaseAll();
      for (const exit of exits) {
        hide(...exit.flames);
        spareFlames.push(exit.flames);
      }
      exits.length = 0;
      sky.clear();
    },
    destroy(): void {
      floor.destroy();
      for (const container of [glows, eyes, exitEyes, sky, flights]) {
        container.destroy({ children: true });
      }
      for (const look of [looks.eye, looks.flame]) {
        look.texture.destroy(true);
      }
    },
  };
}
